"use client"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Search } from "lucide-react"

interface StateEntry {
  state: string
  timeline: string
  agNotification: string
  statute: string
  provisions: string
}

export function StateTableSearch({ stateData }: { stateData: StateEntry[] }) {
  const [search, setSearch] = useState("")

  const filtered = stateData.filter(
    (entry) =>
      entry.state.toLowerCase().includes(search.toLowerCase()) ||
      entry.statute.toLowerCase().includes(search.toLowerCase()) ||
      entry.timeline.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div className="relative mb-8 max-w-md mx-auto">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by state, statute, or timeline..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
        />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-accent/10">
                <th className="text-left p-4 font-semibold text-foreground whitespace-nowrap">State</th>
                <th className="text-left p-4 font-semibold text-foreground whitespace-nowrap">Timeline</th>
                <th className="text-left p-4 font-semibold text-foreground whitespace-nowrap">AG Notification</th>
                <th className="text-left p-4 font-semibold text-foreground whitespace-nowrap">Statute</th>
                <th className="text-left p-4 font-semibold text-foreground">Notable Provisions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry, index) => (
                <tr
                  key={entry.state}
                  className={`border-b border-border/50 ${
                    index % 2 === 0 ? "bg-background" : "bg-card/50"
                  } hover:bg-accent/5 transition-colors`}
                >
                  <td className="p-4 font-semibold text-foreground whitespace-nowrap">{entry.state}</td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        entry.timeline.includes("30 days")
                          ? "bg-red-500/10 text-red-400"
                          : entry.timeline.includes("45 days")
                            ? "bg-orange-500/10 text-orange-400"
                            : entry.timeline.includes("60 days")
                              ? "bg-yellow-500/10 text-yellow-400"
                              : "bg-accent/10 text-accent"
                      }`}
                    >
                      {entry.timeline}
                    </span>
                  </td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap">{entry.agNotification}</td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap font-mono text-xs">
                    {entry.statute}
                  </td>
                  <td className="p-4 text-muted-foreground text-xs leading-relaxed max-w-xs">
                    {entry.provisions}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No states found matching "{search}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground mt-4 text-center">
        Showing {filtered.length} of {stateData.length} jurisdictions
      </p>
    </div>
  )
}
